import { type MediaItem } from "@/mocks/media";
import { typeLabels } from "@/mocks/filters";

type Props = {
  item: MediaItem;
  isFavorite: boolean;
  onToggleFavorite: () => void;
};

const metaRows = (item: MediaItem) => [
  { label: "导演", value: item.director },
  { label: "主演", value: item.cast.join(" / ") },
  { label: "类型", value: item.genres.join(" · ") },
  { label: "地区", value: item.region },
  { label: "年份", value: `${item.year}` },
  { label: "更新", value: `${item.updatedAt.replace(/-/g, ".")} · ${item.status}` },
];

export default function DetailHero({ item, isFavorite, onToggleFavorite }: Props) {
  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <div className="absolute inset-0">
        <img
          src={item.backdrop}
          alt={`${item.title} 剧照`}
          className="h-full w-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/80 to-primary-950/40" />
      </div>

      <div className="relative z-10 w-full px-4 pb-8 pt-8 md:px-6 md:pb-10 md:pt-10">
        <div className="flex flex-col gap-6 sm:flex-row sm:gap-7">
          <div className="relative w-[150px] shrink-0 overflow-hidden rounded-lg md:w-[200px]">
            <div className="aspect-[2/3] w-full bg-background-800">
              <img
                src={item.cover}
                alt={`${item.title} 海报`}
                title={`${item.title} ${typeLabels[item.type]}`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <span className="absolute left-2 top-2 rounded-sm bg-accent-500 px-1.5 py-0.5 font-label text-[10px] font-semibold text-background-50">
              {item.status}
            </span>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-sm bg-background-50/10 px-2 py-0.5 font-label text-[10px] text-background-50/80">
                {typeLabels[item.type]}
              </span>
              <span className="rounded-sm bg-background-50/10 px-2 py-0.5 font-label text-[10px] text-background-50/80">
                {item.region}
              </span>
              <span className="rounded-sm bg-background-50/10 px-2 py-0.5 font-label text-[10px] text-background-50/80">
                {item.year}
              </span>
              <span className="font-label text-[10px] uppercase tracking-[0.18em] text-accent-400">
                {item.enTitle}
              </span>
            </div>

            <h1 className="mt-3 font-heading text-2xl font-black leading-tight tracking-tight text-background-50 md:text-4xl">
              {item.title}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="flex items-baseline gap-1 font-label text-accent-400">
                <span className="text-lg font-bold">{item.rating.toFixed(1)}</span>
                <span className="text-[11px] text-background-50/50">/ 10</span>
              </span>
              <span className="flex items-center gap-1 font-label text-[11px] text-background-50/60">
                <i className="ri-fire-line flex h-3.5 w-3.5 items-center justify-center" />
                热度 {(item.heat / 1000).toFixed(1)}k
              </span>
              <span className="h-3 w-px bg-background-50/25" />
              <span className="font-label text-[11px] text-background-50/60">
                {item.episodes > 1 ? `共 ${item.episodes} 集 · ${item.duration}` : item.duration}
              </span>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
              {metaRows(item).map((row) => (
                <div key={row.label} className="flex gap-3 text-[12px]">
                  <span className="w-10 shrink-0 font-label text-background-50/40">{row.label}</span>
                  <span className="line-clamp-2 text-background-50/80">{row.value}</span>
                </div>
              ))}
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              {item.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-background-50/20 px-2.5 py-0.5 text-[11px] text-background-50/70"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={onToggleFavorite}
                className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-[13px] font-medium transition-colors ${
                  isFavorite
                    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                    : "border border-background-50/30 text-background-50 hover:bg-background-50/10"
                }`}
              >
                <i className={`${isFavorite ? "ri-heart-3-fill" : "ri-heart-3-line"} flex h-4 w-4 items-center justify-center`} />
                {isFavorite ? "已收藏" : "加入收藏"}
              </button>
              <span className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/15 px-4 py-2.5 text-[13px] text-background-50/50">
                <i className="ri-play-circle-line flex h-4 w-4 items-center justify-center" />
                暂不支持在线播放
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}